import { Icon, type IconComponent } from "../Icon";
import styles from "./BottomNavBar.module.css";

/**
 * One destination in the bottom navigation bar.
 *
 * Everything a tab needs travels in this object, so adding a destination is
 * one entry in the host's array — the same shape Android uses, where a new tab
 * is one more `ReflectoTab` enum entry that the bar loops over. No per-tab
 * design asset, no component variant, no change to this file.
 */
export interface NavTab<Id extends string = string> {
  /**
   * Stable identity. Also what `onSelect` reports and `activeId` matches.
   *
   * Generic so a host can pass its own union (`"home" | "clipboard" | …`)
   * and get it back from `onSelect` already narrowed — no cast, and no way
   * for the tab list and the host's route union to drift apart unnoticed.
   * Defaults to `string` for hosts that don't care.
   */
  id: Id;
  /**
   * Already localized by the host — this package never calls an i18n API.
   *
   * Required, and required to be non-empty: it is the visible text AND the
   * button's accessible name. The icon is decorative (rendered without a
   * `label`, so `aria-hidden`), so an empty string here would leave the
   * button nameless to a screen reader. An icon-only tab would need the
   * name moved onto the icon or an `aria-label` on the button — don't get
   * there by passing `label: ""`.
   */
  label: string;
  /** Any component rendering an <svg>; @untitledui/icons satisfies it. */
  icon: IconComponent;
}

export interface BottomNavBarProps<Id extends string = string> {
  /**
   * Destinations, in display order. The host builds this, which is where
   * feature gating belongs: a tab behind a disabled flag is simply absent
   * from the array rather than passed and hidden. That keeps the bar honest
   * about its own width — three tabs get a third each, four get a quarter.
   */
  tabs: readonly NavTab<Id>[];
  /**
   * Currently selected tab id. If it matches no tab — which happens when a
   * destination disappears mid-session, e.g. an SMS tab whose availability
   * depends on traffic seen this service-worker lifetime — the bar simply
   * renders nothing as active. Choosing a fallback is the host's call, since
   * only the host knows which destination is safe to land on.
   */
  activeId: Id;
  onSelect: (id: Id) => void;
  /** Localized accessible name for the <nav> landmark, e.g. "Main". */
  ariaLabel: string;
}

/**
 * BottomNavBar — the side panel / PWA bottom navigation.
 *
 * Presentational and fully data-driven: it renders whatever tabs it is
 * handed. There are no per-destination branches, no `showSmsTab`-style
 * booleans, and no variant flags — the props that exist are the data, the
 * selection, and the callback.
 *
 * **Semantics.** A `<nav>` landmark of buttons carrying `aria-current="page"`,
 * not the ARIA tablist pattern. These destinations swap the whole panel view
 * rather than revealing panels inside one page, and `role="tablist"` would
 * oblige every view to become a labelled `role="tabpanel"` and to implement
 * roving arrow-key focus. Native buttons in a landmark give screen-reader
 * users the right mental model and keep normal Tab order.
 *
 * **Sizing.** Every tab is `flex: 1`, so the bar fills 360px and 400px alike
 * with no fixed item width to fall out of sync with the container.
 */
export function BottomNavBar<Id extends string = string>({
  tabs,
  activeId,
  onSelect,
  ariaLabel,
}: BottomNavBarProps<Id>) {
  // A bar with nothing to navigate to is chrome with no purpose. Render
  // nothing rather than an empty strip that still occupies layout.
  if (tabs.length === 0) return null;

  return (
    <nav className={styles.bar} aria-label={ariaLabel}>
      {tabs.map((tab) => {
        const selected = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            className={selected ? `${styles.tab} ${styles.active}` : styles.tab}
            // aria-current marks the destination you are on. Buttons are not
            // toggles here, so aria-pressed/aria-selected would both misstate it.
            aria-current={selected ? "page" : undefined}
            onClick={() => onSelect(tab.id)}
          >
            {/* The indicator carries the brand gradient when active — the same
                pill-behind-the-icon shape Android's NavigationBarItem uses.
                `tone="inherit"` lets the icon take its colour from the
                indicator, which flips to the fixed dark ink on the gradient. */}
            <span className={styles.indicator}>
              <Icon as={tab.icon} size="md" tone="inherit" />
            </span>
            <span className={styles.label}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
